import {
  Security,
  IPaginationParams,
  Solicitor,
  Contact,
  Tabulator,
  Issuer,
} from "../types";

export type ProxyEventType =
  | "annual_meeting"
  | "special_meeting"
  | "contested_annual_meeting"
  | "contested_special_meeting"
  | "information_statement"
  | "summary_prospectus"
  | "19_a_notice"
  | "annual_report"
  | "semi_annual_report"
  | "consent_solicitation";

export const VOTING_PROXY_EVENT_TYPES: ProxyEventType[] = [
  "annual_meeting",
  "special_meeting",
  "contested_annual_meeting",
  "contested_special_meeting",
  "consent_solicitation",
];

export const INFORMATIONAL_PROXY_EVENT_TYPES: ProxyEventType[] = [
  "information_statement",
  "summary_prospectus",
  "19_a_notice",
  "annual_report",
  "semi_annual_report",
];

export interface IProxyEvent {
  id: string;
  recordDate: string;
  additionalRecordDates: string[];
  operationsStatus: string;
  approvedAt: string | null;
  securities: Security[];
  issuer: Issuer;
  type: ProxyEventType;
  solicitor: Solicitor;
  solicitorContacts: Contact[];
  jobNumber: string;
  tabulator: Tabulator;
  tabulatorContacts: Contact[];
  voteCutoff: string;
  voteCutoffTimezone: string;
  meetingDate: string;
  meetingTimezone: string;
  meetingFormats: MeetingFormat[];
  deliveryMethod: string;
  stats: ProxyEventStats;
  votingStats?: VotingStats;
}

export interface IProxyEventsParams extends IPaginationParams {
  tab?: string | null;
  types?: string[];
  issuerId?: string | null;
  changeRequestStatus?: string | null;
}

export interface IProxyEventParams {
  id: string;
}

export interface IProxyEventMaterialsParams {
  id: string;
}

export type ProxyEventStats = {
  positionCount: number;
  expectedCommunicationCount: number;
  uniqueCustomerCount: number;
  communicationCount: number;
  totalShares: string;
  voteCount: number;
  sharesVoted: string;
  meetingAttendanceCount: number;
  meetingAttendanceGuestAccessCount: number;
  meetingAttendanceLegalProxyCount: number;
  giftedPositionCount: number;
  giftedCommunicationCount: number;
  giftedTotalShares: string;
  giftedVoteCount: number;
  giftedSharesVoted: string;
  nonFullSharePositionCount: number;
  nonFullShareCommunicationCount: number;
  nonFullShareTotalShares: string;
  nonFullShareVoteCount: number;
  nonFullShareSharesVoted: string;
  fullSharePositionCount: number;
  fullShareCommunicationCount: number;
  fullShareTotalShares: string;
  fullShareVoteCount: number;
  fullShareSharesVoted: string;
  initialCount: number;
  initialWaitingToSendCount: number;
  initialSendingCount: number;
  initialSuppressedCount: number;
  initialSentCount: number;
  initialBouncedCount: number;
  initialErrorCount: number;
  initialOpenedCount: number;
  initialClickedCount: number;
  initialStartScheduledSendAt: string | null;
  initialEndScheduledSendAt: string | null;
  initialWebhookCount: number;
  initialWebhookWaitingToSendCount: number;
  initialWebhookSendingCount: number;
  initialWebhookSentCount: number;
  initialWebhookErrorCount: number;
  initialWebhookStartScheduledSendAt: string | null;
  initialWebhookEndScheduledSendAt: string | null;
  giftedInitialCount: number;
  giftedInitialWaitingToSendCount: number;
  giftedInitialSendingCount: number;
  giftedInitialSuppressedCount: number;
  giftedInitialSentCount: number;
  giftedInitialBouncedCount: number;
  giftedInitialErrorCount: number;
  giftedInitialOpenedCount: number;
  giftedInitialClickedCount: number;
  nonFullShareInitialCount: number;
  nonFullShareInitialWaitingToSendCount: number;
  nonFullShareInitialSendingCount: number;
  nonFullShareInitialSuppressedCount: number;
  nonFullShareInitialSentCount: number;
  nonFullShareInitialBouncedCount: number;
  nonFullShareInitialErrorCount: number;
  nonFullShareInitialOpenedCount: number;
  nonFullShareInitialClickedCount: number;
  fullShareInitialCount: number;
  fullShareInitialWaitingToSendCount: number;
  fullShareInitialSendingCount: number;
  fullShareInitialSuppressedCount: number;
  fullShareInitialSentCount: number;
  fullShareInitialBouncedCount: number;
  fullShareInitialErrorCount: number;
  fullShareInitialOpenedCount: number;
  fullShareInitialClickedCount: number;
  efailureCount: number;
  efailureWaitingToSendCount: number;
  efailureSendingCount: number;
  efailureSentCount: number;
  efailureErrorCount: number;
  confirmationCount: number;
  confirmationSendingCount: number;
  confirmationSuppressedCount: number;
  confirmationSentCount: number;
  confirmationBouncedCount: number;
  confirmationErrorCount: number;
  confirmationOpenedCount: number;
  confirmationClickedCount: number;
  confirmationWebhookCount: number;
  confirmationWebhookSendingCount: number;
  confirmationWebhookSentCount: number;
  confirmationWebhookErrorCount: number;
  resentInitialCount: number;
  resentInitialSendingCount: number;
  resentInitialSuppressedCount: number;
  resentInitialSentCount: number;
  resentInitialBouncedCount: number;
  resentInitialErrorCount: number;
  resentInitialOpenedCount: number;
  resentInitialClickedCount: number;
  additionalMailingEmailCount: number;
  additionalMailingEmailSendingCount: number;
  additionalMailingEmailSuppressedCount: number;
  additionalMailingEmailSentCount: number;
  additionalMailingEmailBouncedCount: number;
  additionalMailingEmailErrorCount: number;
  additionalMailingEmailOpenedCount: number;
  additionalMailingEmailClickedCount: number;
  additionalMailingWebhookCount: number;
  additionalMailingWebhookSendingCount: number;
  additionalMailingWebhookSentCount: number;
  additionalMailingWebhookErrorCount: number;
  invoiceDate: string;
  invoiceAmount: string;
  estimatedInvoiceAmount: string;
};

export type VotingStats = {
  voteCount: number;
  customerCount: number;
  positionCount: number;
  sharesVoted: string;
  giftedVoteCount: number;
  giftedSharesVoted: string;
  giftedCustomerCount: number;
  giftedPositionCount: number;
  nonFullSharePositionCount: number;
  nonFullShareCustomerCount: number;
  nonFullShareVoteCount: number;
  nonFullShareSharesVoted: string;
  fullSharePositionCount: number;
  fullShareCustomerCount: number;
  fullShareVoteCount: number;
  fullShareSharesVoted: string;
};

export type MeetingFormat = {
  canVote: string;
  format: MeetingFormatType;
  value: string;
};

export interface IProxyEventProposalsParams {
  id: string;
}

export interface IProxyEventDateStatsParams {
  id: string;
}

export type ProxyEventDateStats = {
  date: string;
  voteCount: number;
  customerCount: number;
  positionCount: number;
  totalSharesVoted: string;
  giftedVoteCount: number;
  giftedSharesVoted: string;
  giftedCustomerCount: number;
  giftedPositionCount: number;
  nonFullSharePositionCount: number;
  nonFullShareCustomerCount: number;
  nonFullShareVoteCount: number;
  nonFullShareSharesVoted: string;
  fullSharePositionCount: number;
  fullShareCustomerCount: number;
  fullShareVoteCount: number;
  fullShareSharesVoted: string;
};

export interface IProposal {
  title: string;
  type: string;
  details: string;
  groupNumber: number;
  proposalNumber: number;
  choices: string[];
  recommendation: string;
  securities: Security[];
  proposalChoiceStats: ProposalChoiceStats[];
}

export type ProposalChoiceStats = {
  choice: string;
  voteCount: number;
  sharesVoted: string;
};

export type MeetingFormatType =
  | "in_person"
  | "virtual"
  | "telephone"
  | "private";

export interface IProxyEventAdditionalMailingForGraph {
  id: string;
  approvedAt: string;
  type: string;
  operationsStatus: string;
  dateReceived: string;
  audience: string;
}

export interface IProxyEventVoteSolicitationForGraph {
  id: string;
  scheduledSendAt: string;
  type: string;
}

export interface IProxyEventAdditionalMailingsParams {
  id: string;
}

export interface IProxyEventVoteSolicitationsParams {
  id: string;
}
